import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Briefcase, CheckCircle2, ArrowRight, Settings, BarChart3, FileText, Users } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '../../components/SEO';

const CONSULTING_IMAGE = 'https://images.unsplash.com/photo-1758873271761-6cfe9b4f000c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwb2ZmaWNlfGVufDB8fHx8MTc3NDUyOTcxNnww&ixlib=rb-4.1.0&q=85';

export const ConsultingServicePage = () => {
  const { t, direction, language } = useLanguage();

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
    { name: language === 'ar' ? 'الاستشارات' : 'HR Consulting' }
  ]);

  const services = [
    {
      icon: FileText,
      title: 'HR Policies & Procedures',
      description: 'Development and implementation of comprehensive HR policies aligned with best practices and local regulations.',
    },
    {
      icon: Settings,
      title: 'Organizational Design',
      description: 'Restructuring and optimization of organizational structures to improve efficiency and performance.',
    },
    {
      icon: Users,
      title: 'Talent Strategy',
      description: 'Strategic workforce planning, succession planning, and talent acquisition strategies.',
    },
    {
      icon: BarChart3,
      title: 'Performance Systems',
      description: 'Design and implementation of KPI frameworks and performance management systems.',
    },
    {
      icon: Briefcase,
      title: 'HR Operations',
      description: 'HR process optimization, systems implementation, and operational excellence.',
    },
    {
      icon: CheckCircle2,
      title: 'Training Needs Analysis',
      description: 'Comprehensive assessment of organizational training requirements and development roadmaps.',
    },
  ];

  const benefits = [
    'Tailored solutions for your specific business challenges',
    'Experienced consultants with diverse industry backgrounds',
    'Data-driven insights and recommendations',
    'Implementation support and change management',
    'Knowledge transfer to your internal team',
    'Ongoing support and follow-up',
  ];

  const whoItIsFor = [
    'Companies undergoing organizational transformation',
    'Startups building HR foundations',
    'Organizations seeking compliance with regulations',
    'Businesses optimizing HR operations',
    'Companies expanding to new markets',
  ];

  return (
    <div className="min-h-screen" data-testid="consulting-service-page">
      <SEO
        title="HR Consulting Services - Strategic HR Solutions"
        titleAr="استشارات الموارد البشرية - حلول استراتيجية"
        description="WeDo HR consulting services help organizations transform HR operations. Policies, organizational design, talent strategy, and performance management across Europe, Middle East, and GCC."
        descriptionAr="تساعد خدمات استشارات الموارد البشرية من ويدو المؤسسات في تحويل عمليات الموارد البشرية. السياسات والتصميم التنظيمي واستراتيجية المواهب وإدارة الأداء في أوروبا والشرق الأوسط ودول الخليج."
        keywords="HR consulting, organizational design, HR policies, talent strategy, performance management, استشارات موارد بشرية, تصميم تنظيمي"
        canonicalUrl="/services/consulting"
        structuredData={breadcrumbSchema}
        language={language}
      />
      {/* Hero Section */}
      <section className="bg-white border-b border-[#E5E7EB] py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] mb-6">
                {t('services.consulting.title')}
              </h1>
              <p className="text-xl text-[#6B7280] mb-8">
                {t('services.consulting.description')}
              </p>
              <Link to="/contact">
                <Button size="lg" className="btn-primary" data-testid="contact-btn">
                  {t('services.consulting.cta')}
                  <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                </Button>
              </Link>
            </div>
            <div className="hidden lg:block">
              <img 
                src={CONSULTING_IMAGE} 
                alt="Consulting services" 
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
              Our Consulting Services
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              Comprehensive HR consulting to transform your organization
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="card-hover" data-testid={`service-${index}`}>
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center mb-5">
                    <service.icon className="h-7 w-7 text-[#33AADD]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#111827] mb-3">{service.title}</h3>
                  <p className="text-[#6B7280]">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-6">
                Why Partner With Us?
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`benefit-${index}`}>
                    <CheckCircle2 className="h-5 w-5 text-[#33AADD] flex-shrink-0 mt-0.5" />
                    <span className="text-[#6B7280]">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Card className="bg-[#F9FAFB] border-none">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-[#111827] mb-6">Who Is This For?</h3>
                  <ul className="space-y-4">
                    {whoItIsFor.map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#33AADD]" />
                        <span className="text-[#6B7280]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#33AADD]">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Transform Your HR?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help optimize your HR operations and drive business results.
          </p>
          <Link to="/contact">
            <Button size="lg" variant="secondary" className="bg-white text-[#33AADD] hover:bg-white/90" data-testid="cta-btn">
              Schedule a Consultation
              <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ConsultingServicePage;
